import { TeamResource } from 'dy-frontend-http-repository/lib/modules/Team/resources';
import { useStore } from 'effector-react';
import React, { useEffect } from 'react';

import Circle from '@app/components/Circle';
import Select from '@app/components/Select';
import { ToastUtils } from '@app/data/utils';
import { useCustomSelectFormField, useForm } from '@app/hooks';
import { getStringNotEqualToStringValidator, getStringRequiredValidator } from '@app/hooks/validation/functions';
import { Button, Card, Classes, FormGroup, Intent, MenuItem } from '@blueprintjs/core';
import { ItemRendererProps } from '@blueprintjs/select';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import Overlay from '@components/Overlay';
import DevText from '@components/Text';
import { ModalProps } from '@modals/types';
import { taskApi } from '@pages/Task/store/apis';
import { assignPrimaryTeam, fetchTeams } from '@pages/Task/store/effects';

import { TaskShadowIdAssignType } from '../../enums';
import { setTaskShadowIdGenerationInformation } from '../../store/events';
import { $teams } from '../../store/states';

export interface AssignPrimaryTeamModalProps {
    taskId: ID;
    teamId?: ID;
}

type Props = ModalProps<AssignPrimaryTeamModalProps>;

const selectedTeamValidators = [getStringRequiredValidator()];
const getSelectedTeamValidators = (teamId: AssignPrimaryTeamModalProps['teamId']) => {
    if (!teamId) {
        return selectedTeamValidators;
    }

    return [
        ...selectedTeamValidators,
        getStringNotEqualToStringValidator({
            stringToCompareWith: teamId,
            stringNotEqualToStringMessage: 'This team is alredy set a primary',
        }),
    ];
};

const AssignPrimaryTeamModal: React.FC<Props> = ({ closeModal, data }) => {
    const teams = useStore($teams);

    const selectedTeamId = useCustomSelectFormField<ID | null>({
        id: 'selected-team-id',
        initialValue: data?.teamId ?? null,
        formatValue: (date) => (date ? date.toString() : ''),
        validators: getSelectedTeamValidators(data?.teamId),
    });

    const form = useForm({
        fields: [selectedTeamId],
        apiCall: async () => {
            if (!data) {
                return { response: false };
            }

            if (!teams) {
                return { response: false };
            }

            try {
                // Assign primary team
                await assignPrimaryTeam({ id: data.taskId, input: { team_id: selectedTeamId.value! } });

                // Update primary team local state information
                const team = teams.items.find((t) => t.id === selectedTeamId.value);
                if (team) {
                    taskApi.setPrimaryTeam({ team: { id: team.id, title: team.title, color: team.color } });
                }

                // Set task shadow ID generation information
                if (data.teamId) {
                    // Reassign
                    setTaskShadowIdGenerationInformation({ type: TaskShadowIdAssignType.REASSIGN });
                } else {
                    // First assign
                    setTaskShadowIdGenerationInformation({ type: TaskShadowIdAssignType.ASSIGN });
                }

                // Close modal
                closeModal?.();

                return { response: true };
            } catch (e) {
                // TODO: handle error
                throw e;
            }
        },
        onFailure: (e) => {
            // TODO: handle error
            console.error(e);
            ToastUtils.showToast({ message: e, intent: Intent.DANGER });
        },
    });

    useEffect(() => {
        if (teams !== null) {
            return;
        }

        fetchTeams().catch((e) => {
            // TODO: handle error
            console.error(e);
        });
    }, []);

    if (!data) {
        closeModal?.();
        return null;
    }

    const renderTeamSelector = () => {
        const renderItem = (item: TeamResource, { handleClick }: ItemRendererProps) => {
            const isActive = item.id === selectedTeamId.value;

            return (
                <MenuItem
                    active={isActive}
                    key={item.id}
                    text={item.title}
                    icon={<Circle size="16px" color={item.color} />}
                    onClick={handleClick}
                />
            );
        };

        const handleItemSelect = (item: TeamResource) => {
            selectedTeamId.handleChange(item.id);
        };

        const selectedTeam = teams?.items.find((t) => t.id === selectedTeamId.value);
        const teamTitle = selectedTeam?.title ?? 'Unknown team';

        return (
            <FormGroup label="Select team to assign as primary">
                <Select<TeamResource>
                    className={teams === null ? Classes.SKELETON : ''}
                    items={teams?.items ?? []}
                    itemRenderer={renderItem}
                    onItemSelect={handleItemSelect}
                    popoverProps={{ matchTargetWidth: true, usePortal: false }}
                    selectButtonProps={{
                        fill: true,
                        rightIcon: 'double-caret-vertical',
                        placeholder: 'Select primary team',
                        icon: selectedTeam && <Circle size="16px" color={selectedTeam.color} />,
                        text: selectedTeamId.value ? teamTitle : '',
                    }}
                />
            </FormGroup>
        );
    };

    return (
        <Overlay isOpen onClose={closeModal}>
            <Card style={{ width: '558px' }}>
                <Flex className="mb-2" align="start" justify="space-between">
                    <div>
                        <Heading type="h4" className="mb-1">
                            Select primary team
                        </Heading>
                        <DevText muted>Select primary team, responsible for this request</DevText>
                    </div>
                    <Button minimal icon="cross" onClick={closeModal} disabled={form.isSubmitting} />
                </Flex>

                <DevText running className="mb-2">
                    After assigning primary team, request will receive unique ID
                </DevText>

                {renderTeamSelector()}

                <form onSubmit={form.handleFormSubmit}>
                    <Flex align="center" justify="space-between">
                        <DevText muted>Proceed with assigning?</DevText>
                        <div>
                            <Button minimal onClick={closeModal} className="mr-1" disabled={form.isSubmitting}>
                                No, cancel
                            </Button>
                            <Button
                                type="submit"
                                intent={Intent.PRIMARY}
                                loading={form.isSubmitting}
                                disabled={form.isSubmitting}
                            >
                                Assign team
                            </Button>
                        </div>
                    </Flex>
                </form>
            </Card>
        </Overlay>
    );
};

export default AssignPrimaryTeamModal;
